import { audioContext } from '@/audio/context';
import { recordedSheets } from '@/audio/recorded';
import { SheetLibrary, SheetVoice, WebVoice } from '@/audio/voice';
import { Listener } from '@/speech/audio/listener';

/**
 * The audio lab (debug tools, issue #39: the voice crackles on iPhone while the microphone is
 * open). Builds one audio setup at a time from independent choices (audio session, context,
 * capture worklet, microphone constraints, how the sound is played), plays a test sound through it,
 * and reports what the browser actually did, so a tester can rate each setup clean or crackly.
 */

export type SessionChoice = 'untouched' | 'auto' | 'playback' | 'play-and-record' | 'game';
export type ContextChoice = 'shared' | 'fresh-before-mic' | 'fresh-after-mic';
export type WorkletChoice = 'before-mic' | 'after-mic' | 'none';
export type Sound = 'line-game' | 'line-plain' | 'tone' | 'element';
/** What happens to the open microphone while the sound plays. */
export type DuringPlayback =
  | 'keep'
  | 'disable-track'
  | 'stop-track'
  | 'stop-track-playback'
  /** Tracks stopped and the mic's nodes disconnected from the context, playback session. */
  | 'release-all'
  | 'ec-off';

export interface LabConfig {
  /** The audio session before anything starts; 'game' = playback, then play-and-record at the mic. */
  session: SessionChoice;
  context: ContextChoice;
  /** New contexts only. */
  sampleRate: 'default' | '44100' | '48000';
  latencyHint: 'default' | 'interactive' | 'balanced' | 'playback';
  worklet: WorkletChoice;
  mic: boolean;
  echoCancellation: 'on' | 'off' | 'exact-on';
  noiseSuppression: 'off' | 'on' | 'exact-off';
  autoGainControl: boolean;
  sound: Sound;
  duringPlayback: DuringPlayback;
  /** Output level of the tone and plain playback (1 = full scale; the voice lines peak at 0.7). */
  level: '0.03' | '0.1' | '0.25' | '0.7';
  /** The microphone's worklet on the playback context, or on a second context of its own. */
  capture: 'same-context' | 'separate-context';
  /** Wait after changing the microphone (duringPlayback) before the sound starts. */
  settleMs: '0' | '300' | '1000' | '2000';
  /**
   * Where the tone and plain playback go: the context's speakers, or a MediaStream played by an
   * audio element (srcObject), or that stream sent through a local WebRTC call (as call apps play
   * the other person's voice).
   */
  output: 'destination' | 'stream-element' | 'webrtc-loopback';
  /**
   * Web Audio while the sound plays (the audio element only, which needs no context): running as
   * usual, suspended, or closed (new contexts only; the setup must be built again after).
   */
  webAudio: 'running' | 'suspended' | 'closed';
}

/** The game's own setup (createPracticeSession, sources.ts) with the voice as it plays lines. */
export const GAME: LabConfig = {
  session: 'game',
  context: 'shared',
  sampleRate: 'default',
  latencyHint: 'default',
  worklet: 'before-mic',
  mic: true,
  echoCancellation: 'on',
  noiseSuppression: 'off',
  autoGainControl: true,
  sound: 'line-game',
  duringPlayback: 'keep',
  level: '0.25',
  capture: 'same-context',
  settleMs: '0',
  output: 'destination',
  webAudio: 'running',
};

export interface Preset {
  id: string;
  label: string;
  /** What the setup isolates. */
  why: string;
  config: Partial<LabConfig>;
}

/** The test matrix: each row changes one thing from the game's setup. */
export const PRESETS: readonly Preset[] = [
  {
    id: 'A',
    label: 'Microphone off',
    why: 'Baseline: playback alone (expected clean).',
    config: { mic: false, worklet: 'none' },
  },
  { id: 'B', label: 'As in the game', why: 'Reproduces the crackle.', config: {} },
  {
    id: 'C',
    label: 'play-and-record from the start',
    why: 'No session switch at the mic (playback, then play-and-record, today).',
    config: { session: 'play-and-record' },
  },
  {
    id: 'D',
    label: 'Echo cancellation off',
    why: "iOS voice processing (the echo canceller's audio unit).",
    config: { echoCancellation: 'off' },
  },
  {
    id: 'E',
    label: 'Mic open, no capture worklet',
    why: 'The worklet and its zero-gain sink to the output.',
    config: { worklet: 'none' },
  },
  {
    id: 'F',
    label: 'Worklet added after the mic',
    why: 'Control: the known WebKit bug; should crackle.',
    config: { worklet: 'after-mic', context: 'fresh-before-mic' },
  },
  {
    id: 'G',
    label: 'New context after the mic',
    why: 'Context created at the rate the mic switched the hardware to (no worklet: it could only load after the mic, the known bug).',
    config: { context: 'fresh-after-mic', worklet: 'none' },
  },
  {
    id: 'H',
    label: 'New 48 kHz context',
    why: 'Fixed context rate (iOS hardware usually runs at 48 kHz).',
    config: { context: 'fresh-before-mic', sampleRate: '48000' },
  },
  {
    id: 'I',
    label: 'latencyHint playback',
    why: 'Larger output buffers (if the browser honours the hint).',
    config: { context: 'fresh-before-mic', latencyHint: 'playback' },
  },
  {
    id: 'J',
    label: 'Audio element, mic on',
    why: 'Playback outside Web Audio.',
    config: { sound: 'element' },
  },
  {
    id: 'K',
    label: 'Plain playback (no gain ramps)',
    why: "The voice's crossfade gain automation.",
    config: { sound: 'line-plain' },
  },
  {
    id: 'L',
    label: 'Tone, mic on',
    why: 'A pure 440 Hz tone: crackle is easiest to hear on it.',
    config: { sound: 'tone' },
  },
  {
    id: 'M',
    label: 'Exact constraints',
    why: 'echoCancellation exact true, noiseSuppression exact false.',
    config: { echoCancellation: 'exact-on', noiseSuppression: 'exact-off' },
  },
  {
    id: 'N',
    label: 'Auto gain off',
    why: 'Input level control.',
    config: { autoGainControl: false },
  },
  {
    id: 'O',
    label: 'Mic track paused while playing',
    why: 'track.enabled = false during the sound (does the echo canceller stand down?).',
    config: { duringPlayback: 'disable-track' },
  },
  {
    id: 'P',
    label: 'Mic stopped while playing, reopened after',
    why: 'Half duplex: no echo canceller while the app speaks; times the reopen.',
    config: { duringPlayback: 'stop-track' },
  },
  {
    id: 'Q',
    label: 'Mic stopped + playback session while playing',
    why: 'As P, and the session switched to playback for the sound (louder speaker?).',
    config: { duringPlayback: 'stop-track-playback' },
  },
  {
    id: 'R',
    label: 'Echo cancellation off while playing',
    why: 'applyConstraints switches it off for the sound and back on after.',
    config: { duringPlayback: 'ec-off' },
  },
  {
    id: 'S',
    label: 'Very quiet tone, mic on',
    why: 'Is it loudness? A tone at 3% of full scale with echo cancellation on.',
    config: { sound: 'tone', level: '0.03' },
  },
  {
    id: 'T',
    label: 'Loud tone, echo cancellation off',
    why: 'Was D clean only because it was quiet? The tone at 70% with echo cancellation off.',
    config: { sound: 'tone', level: '0.7', echoCancellation: 'off' },
  },
  {
    id: 'U',
    label: 'As in the game, with headphones',
    why: 'Plug in or connect headphones first: is it the speaker while the mic listens?',
    config: {},
  },
  {
    id: 'V',
    label: 'Capture on its own context',
    why: 'The mic and its worklet on a second AudioContext; the voice plays on the shared one.',
    config: { capture: 'separate-context' },
  },
  {
    id: 'W',
    label: 'Q, then wait 1 s',
    why: 'Mic stopped, playback session, 1 s before the sound: does the volume come back?',
    config: { duringPlayback: 'stop-track-playback', settleMs: '1000' },
  },
  {
    id: 'X',
    label: 'Q, then wait 2 s',
    why: 'As W with 2 s: how long iOS takes to leave its call-style audio mode.',
    config: { duringPlayback: 'stop-track-playback', settleMs: '2000' },
  },
  {
    id: 'Y',
    label: 'P, then wait 1 s',
    why: 'Mic stopped, session left at play-and-record, 1 s before the sound.',
    config: { duringPlayback: 'stop-track', settleMs: '1000' },
  },
  {
    id: 'Z',
    label: 'Mic fully released while playing',
    why: 'As Q, and the mic source node and worklet disconnected too (as in A, which sounded normal).',
    config: { duringPlayback: 'release-all' },
  },
  {
    id: 'Z1',
    label: 'Fully released, then wait 300 ms',
    why: 'As Z, with a short wait before the sound.',
    config: { duringPlayback: 'release-all', settleMs: '300' },
  },
  {
    id: 'S1',
    label: 'Tone as a media stream, mic on',
    why: 'The tone played by an audio element from a MediaStream (srcObject), as calls play voices.',
    config: { sound: 'tone', output: 'stream-element' },
  },
  {
    id: 'S2',
    label: 'Line as a media stream, mic on',
    why: 'As S1 with the voice line.',
    config: { sound: 'line-plain', output: 'stream-element' },
  },
  {
    id: 'S3',
    label: 'Line through a local WebRTC call, mic on',
    why: 'The voice line sent through a local peer connection and played as the remote track.',
    config: { sound: 'line-plain', output: 'webrtc-loopback' },
  },
  {
    id: 'S4',
    label: 'Tone through a local WebRTC call, mic on',
    why: 'As S3 with the tone.',
    config: { sound: 'tone', output: 'webrtc-loopback' },
  },
  {
    id: 'N1',
    label: 'Audio element, mic on, Web Audio suspended',
    why: 'Mic open with echo cancellation, no AudioContext rendering: is Web Audio running beside the mic the problem?',
    config: { sound: 'element', webAudio: 'suspended' },
  },
  {
    id: 'N2',
    label: 'Audio element, mic on, Web Audio closed',
    why: 'As N1 with the context closed (build again after each run).',
    config: { sound: 'element', webAudio: 'closed', context: 'fresh-before-mic' },
  },
];

export const presetConfig = (p: Preset): LabConfig => ({ ...GAME, ...p.config });

/** What the browser reported for a setup. */
export type Diagnostics = Record<string, string>;

type AudioSessionNavigator = Navigator & { audioSession?: { type: string } };
const audioSession = () => (navigator as AudioSessionNavigator).audioSession;
export const sessionType = () => audioSession()?.type ?? 'not supported';

function setSession(type: string) {
  const session = audioSession();
  try {
    if (session) session.type = type;
  } catch {
    // not supported
  }
}

function constraints(config: LabConfig): MediaTrackConstraints {
  const ec = config.echoCancellation;
  const ns = config.noiseSuppression;
  return {
    channelCount: 1,
    echoCancellation: ec === 'exact-on' ? { exact: true } : ec === 'on',
    noiseSuppression: ns === 'exact-off' ? { exact: false } : ns === 'on',
    autoGainControl: config.autoGainControl,
  };
}

const MANIFEST = `${import.meta.env.BASE_URL}voice/manifest.json`;

/**
 * Resumes a context, waiting at most 2 s: without an output device, or while iOS holds the audio
 * (a call, another app), resume() may not settle. The context's state goes in the report.
 */
async function resumeSoon(context: AudioContext): Promise<void> {
  if (context.state === 'running') return;
  await Promise.race([
    context.resume(),
    new Promise<void>((resolve) => {
      setTimeout(resolve, 2000);
    }),
  ]);
}

/** A built setup: torn down before the next one. */
export class AudioLab {
  private context: AudioContext | null = null;
  private owned = false;
  private stream: MediaStream | null = null;
  private config: LabConfig = GAME;
  /** The second context for capture (`capture: 'separate-context'`). */
  private captureContext: AudioContext | null = null;
  private listener: Listener | null = null;
  private readonly libraries = new Map<AudioContext, Promise<SheetLibrary | undefined>>();
  private stopPlayback: (() => void) | null = null;

  /** Builds `config` (call from a tap: it may start audio and open the microphone). */
  async build(config: LabConfig, log: (line: string) => void): Promise<Diagnostics> {
    await this.teardown();
    this.config = config;
    const diag: Diagnostics = { 'session before': sessionType() };
    if (config.session === 'game') setSession('playback');
    else if (config.session !== 'untouched') setSession(config.session);

    const fresh = (): AudioContext => {
      const options: AudioContextOptions = {};
      if (config.sampleRate !== 'default') options.sampleRate = Number(config.sampleRate);
      if (config.latencyHint !== 'default') options.latencyHint = config.latencyHint;
      this.owned = true;
      return new AudioContext(options);
    };
    if (config.context === 'shared') this.context = audioContext();
    else if (config.context === 'fresh-before-mic') this.context = fresh();
    const early = this.context;
    if (early) {
      await resumeSoon(early);
      if (config.worklet === 'before-mic') {
        log('loading the capture worklet');
        await Listener.prepare(early);
      }
    }

    if (config.mic && config.capture === 'separate-context') {
      this.captureContext = new AudioContext();
      await resumeSoon(this.captureContext);
      if (config.worklet !== 'none') await Listener.prepare(this.captureContext);
    }
    if (config.mic) {
      if (config.session === 'game') setSession('play-and-record');
      log('opening the microphone');
      try {
        this.stream = await navigator.mediaDevices.getUserMedia({ audio: constraints(config) });
      } catch (e) {
        diag.microphone = `failed: ${String(e)}`;
      }
    }

    this.context ??= fresh();
    const context = this.context;
    await resumeSoon(context);
    if (this.stream && config.worklet !== 'none') {
      // A context made after the mic can only get its worklet after the mic.
      const late = config.worklet === 'after-mic' || !early;
      diag.worklet = late ? 'loaded after the mic' : 'loaded before the mic';
      if (late) log('loading the capture worklet (after the mic)');
      this.listener = await Listener.start(this.stream, {}, this.captureContext ?? context);
    } else diag.worklet = 'none';
    if (this.captureContext)
      diag['capture context'] =
        `separate, ${this.captureContext.state}, ${String(this.captureContext.sampleRate)} Hz`;
    diag.setSinkId = [
      'setSinkId' in HTMLMediaElement.prototype ? 'audio element yes' : 'audio element no',
      'setSinkId' in AudioContext.prototype ? 'AudioContext yes' : 'AudioContext no',
    ].join(', ');

    diag.context = `${config.context}, ${context.state}, ${String(context.sampleRate)} Hz`;
    diag['base latency'] = `${(context.baseLatency * 1000).toFixed(1)} ms`;
    if ('outputLatency' in context)
      diag['output latency'] = `${(context.outputLatency * 1000).toFixed(1)} ms`;
    diag['session after'] = sessionType();
    const track = this.stream?.getAudioTracks()[0];
    if (track) {
      const s = track.getSettings() as MediaTrackSettings & { latency?: number };
      diag.track = [
        `${String(s.sampleRate ?? '?')} Hz`,
        `${String(s.channelCount ?? '?')} ch`,
        `EC ${String(s.echoCancellation ?? '?')}`,
        `NS ${String(s.noiseSuppression ?? '?')}`,
        `AGC ${String(s.autoGainControl ?? '?')}`,
        s.latency !== undefined ? `latency ${(s.latency * 1000).toFixed(0)} ms` : '',
      ]
        .filter(Boolean)
        .join(', ');
    }
    const supported = navigator.mediaDevices.getSupportedConstraints();
    diag.supported = ['echoCancellation', 'noiseSuppression', 'autoGainControl', 'sampleRate']
      .filter((k) => supported[k as keyof MediaTrackSupportedConstraints])
      .join(', ');
    return diag;
  }

  private library(context: AudioContext): Promise<SheetLibrary | undefined> {
    if (context === audioContext()) return recordedSheets();
    let lib = this.libraries.get(context);
    if (!lib) {
      lib = SheetLibrary.load(MANIFEST, () => context);
      this.libraries.set(context, lib);
    }
    return lib;
  }

  /**
   * Plays the test sound through the built setup; resolves when it ends, with the audio clock's
   * pace against the wall clock (an audio thread that misses deadlines falls behind).
   */
  async play(sound: Sound, text: string, log: (line: string) => void): Promise<Diagnostics> {
    const context = this.context;
    if (!context) throw new Error('build a setup first');
    this.stopPlayback?.();
    const diag: Diagnostics = {};
    await this.beforeSound(diag, log);
    if (sound !== 'tone' && sound !== 'element') log('decoding the voice sheets');
    const library = sound === 'tone' ? undefined : await this.library(context);
    await this.webAudioOff(diag);
    const stats = () =>
      (context as AudioContext & { playbackStats?: Record<string, number> }).playbackStats;
    const before = { ...(stats() ?? {}) };
    const t0 = context.currentTime;
    const w0 = performance.now();
    const playing = (async () => {
      if (sound === 'tone' || sound === 'line-plain') await this.openOutput(context, diag);
      if (sound === 'tone') await this.tone(context);
      else if (!library) throw new Error('the voice sheets did not load');
      else if (sound === 'line-game') {
        const voice = new SheetVoice(
          () => context,
          new WebVoice(() => context),
          Promise.resolve(library),
        );
        await Promise.resolve(); // the voice takes the library on the next tick
        this.stopPlayback = () => {
          voice.stop();
        };
        await voice.play([{ kind: 'say', text }]);
      } else if (sound === 'line-plain') await this.plain(context, library, text);
      else await this.element(library, text);
    })();
    // Bounded by the sound's length: without a running audio clock (no output device, a
    // suspended context) the sound never ends, and the lab must not hang on it.
    const seconds =
      sound === 'tone'
        ? 3
        : (library?.index.find(text) ?? []).reduce(
            (sum, { range }) => sum + range.end - range.start,
            0,
          );
    // The audio element first loads the sheet's metadata over the network.
    const viaStream =
      this.config.output !== 'destination' && (sound === 'tone' || sound === 'line-plain');
    const limit = seconds + (sound === 'element' ? 15 : viaStream ? 6 : 2);
    const ended = await Promise.race([
      playing.then(() => true),
      new Promise<boolean>((resolve) => {
        setTimeout(() => {
          resolve(false);
        }, limit * 1000);
      }),
    ]);
    if (!ended) {
      this.stop();
      diag.ended = `no end after ${limit.toFixed(1)} s (the audio clock did not run?)`;
    }
    // A stream output lags (element and jitter buffers): let its tail play out first.
    if (viaStream && ended)
      await new Promise((resolve) => {
        setTimeout(resolve, 500);
      });
    this.closeOutput();
    const wall = (performance.now() - w0) / 1000;
    await this.webAudioOn();
    await this.afterSound(diag, log);
    const audio = context.currentTime - t0;
    if (sound !== 'element' && wall > 0.5)
      diag['audio clock'] = `${(audio / wall).toFixed(3)} × wall clock (${wall.toFixed(1)} s)`;
    // Chrome only: output underruns (glitches) during this sound.
    const after = stats();
    if (after) {
      const events = (after.underrunEvents ?? 0) - (before.underrunEvents ?? 0);
      const ms = ((after.underrunDuration ?? 0) - (before.underrunDuration ?? 0)) * 1000;
      diag.underruns = `${String(events)} (${ms.toFixed(0)} ms) during the sound`;
    }
    return diag;
  }

  private tone(context: AudioContext): Promise<void> {
    const osc = context.createOscillator();
    const gain = context.createGain();
    osc.frequency.value = 440;
    const t = context.currentTime + 0.05;
    gain.gain.setValueAtTime(0, t);
    const level = Number(this.config.level);
    gain.gain.linearRampToValueAtTime(level, t + 0.03);
    gain.gain.setValueAtTime(level, t + 2.97);
    gain.gain.linearRampToValueAtTime(0, t + 3);
    osc.connect(gain).connect(this.out ?? context.destination);
    osc.start(t);
    osc.stop(t + 3);
    return new Promise((resolve) => {
      this.stopPlayback = () => {
        osc.stop();
      };
      osc.onended = () => {
        resolve();
      };
    });
  }

  /** The line's ranges back to back with no gain nodes (hard joins). */
  private plain(context: AudioContext, library: SheetLibrary, text: string): Promise<void> {
    const segments = library.index.find(text);
    if (!segments?.length) return Promise.reject(new Error(`not recorded: ${text}`));
    let at = context.currentTime + 0.05;
    const level = context.createGain();
    level.gain.value = Number(this.config.level) / 0.7;
    level.connect(this.out ?? context.destination);
    const sources = segments.map(({ sheet, range }) => {
      const source = context.createBufferSource();
      source.buffer = library.buffers[sheet] ?? null;
      source.connect(level);
      source.start(at, range.start, range.end - range.start);
      at += range.end - range.start;
      return source;
    });
    return new Promise((resolve) => {
      this.stopPlayback = () => {
        for (const s of sources) s.stop();
      };
      const last = sources.at(-1);
      if (last)
        last.onended = () => {
          resolve();
        };
    });
  }

  /** The line's first range through an HTML audio element (no Web Audio). */
  private async element(library: SheetLibrary, text: string): Promise<void> {
    const segment = library.index.find(text)?.[0];
    if (!segment) throw new Error(`not recorded: ${text}`);
    const manifest = (await (await fetch(MANIFEST)).json()) as { sheets: { file: string }[] };
    const file = manifest.sheets[segment.sheet]?.file ?? '';
    const audio = new Audio(`${import.meta.env.BASE_URL}voice/${file}`);
    audio.preload = 'auto';
    await new Promise<void>((resolve, reject) => {
      audio.onloadedmetadata = () => {
        resolve();
      };
      audio.onerror = () => {
        reject(new Error('the audio element could not load the sheet'));
      };
    });
    audio.currentTime = segment.range.start;
    await audio.play();
    await new Promise<void>((resolve) => {
      const timer = setInterval(() => {
        if (audio.currentTime >= segment.range.end || audio.paused) {
          clearInterval(timer);
          audio.pause();
          resolve();
        }
      }, 25);
      this.stopPlayback = () => {
        audio.pause();
      };
    });
  }

  /** The node the tone and plain playback connect to (`output`); null: the context's speakers. */
  private out: AudioNode | null = null;
  private closeOutput: () => void = () => undefined;

  /** Sets up `output`: a MediaStream for an audio element, directly or through a local call. */
  private async openOutput(context: AudioContext, diag: Diagnostics): Promise<void> {
    this.closeOutput();
    this.out = null;
    const mode = this.config.output;
    if (mode === 'destination') return;
    const t = performance.now();
    const dest = context.createMediaStreamDestination();
    const element = new Audio();
    element.setAttribute('playsinline', '');
    element.autoplay = true;
    let peers: RTCPeerConnection[] = [];
    if (mode === 'stream-element') element.srcObject = dest.stream;
    else {
      const sender = new RTCPeerConnection();
      const receiver = new RTCPeerConnection();
      peers = [sender, receiver];
      sender.onicecandidate = (e) => {
        if (e.candidate) void receiver.addIceCandidate(e.candidate);
      };
      receiver.onicecandidate = (e) => {
        if (e.candidate) void sender.addIceCandidate(e.candidate);
      };
      const remote = new Promise<MediaStream>((resolve) => {
        receiver.ontrack = (e) => {
          resolve(e.streams[0] ?? new MediaStream([e.track]));
        };
      });
      for (const track of dest.stream.getAudioTracks()) sender.addTrack(track, dest.stream);
      const offer = await sender.createOffer();
      await sender.setLocalDescription(offer);
      await receiver.setRemoteDescription(offer);
      const answer = await receiver.createAnswer();
      await receiver.setLocalDescription(answer);
      await sender.setRemoteDescription(answer);
      element.srcObject = await remote;
      // Wait for the connection, so the start of the sound is not lost.
      await new Promise<void>((resolve) => {
        const check = () => {
          if (receiver.connectionState === 'connected') resolve();
        };
        receiver.onconnectionstatechange = check;
        check();
        setTimeout(resolve, 3000);
      });
      diag.webrtc = receiver.connectionState;
    }
    try {
      await element.play();
    } catch (e) {
      diag['output element'] = `play failed: ${String(e)}`;
    }
    this.out = dest;
    diag.output = `${mode} (ready in ${(performance.now() - t).toFixed(0)} ms)`;
    this.closeOutput = () => {
      element.pause();
      element.srcObject = null;
      for (const pc of peers) pc.close();
      dest.disconnect();
      this.out = null;
      this.closeOutput = () => undefined;
    };
  }

  /** Suspends or closes Web Audio for an audio-element sound (`webAudio`). */
  private async webAudioOff(diag: Diagnostics): Promise<void> {
    const mode = this.config.webAudio;
    if (mode === 'running' || this.config.sound !== 'element') return;
    // The app's shared context too: every tap unlocks (resumes) it, wherever the lab plays.
    const contexts = [...new Set([this.context, this.captureContext, audioContext()])].filter(
      (c): c is AudioContext => !!c,
    );
    for (const c of contexts) {
      const ours = c === this.captureContext || (c === this.context && this.owned);
      if (mode === 'closed' && ours) {
        if (c === this.listener?.audioContext) {
          await this.listener.close();
          this.listener = null;
        }
        await c.close();
      } else await c.suspend();
    }
    diag['Web Audio while playing'] = contexts.map((c) => c.state).join(', ') || 'none';
  }

  /** Resumes suspended contexts after the sound; closed ones need a new build. */
  private async webAudioOn(): Promise<void> {
    if (this.config.webAudio === 'running' || this.config.sound !== 'element') return;
    for (const c of new Set([this.context, this.captureContext, audioContext()])) {
      if (c?.state === 'suspended') await resumeSoon(c);
    }
    if (this.context?.state === 'closed') {
      this.libraries.delete(this.context);
      this.context = null;
      this.owned = false;
    }
    if (this.captureContext?.state === 'closed') this.captureContext = null;
  }

  /** Applies `duringPlayback` to the open microphone before the sound. */
  private async beforeSound(diag: Diagnostics, log: (line: string) => void): Promise<void> {
    const mode = this.config.duringPlayback;
    const track = this.stream?.getAudioTracks()[0];
    if (mode === 'keep' || !track) return;
    const t = performance.now();
    if (mode === 'disable-track') track.enabled = false;
    else if (mode === 'ec-off') {
      log('echo cancellation off');
      try {
        await track.applyConstraints({ ...constraints(this.config), echoCancellation: false });
      } catch (e) {
        diag['EC off'] = `failed: ${String(e)}`;
      }
      diag['EC while playing'] = String(track.getSettings().echoCancellation ?? '?');
    } else {
      log('stopping the mic');
      if (mode === 'release-all') {
        await this.listener?.close();
        this.listener = null;
      }
      for (const tr of this.stream?.getTracks() ?? []) tr.stop();
      if (mode !== 'stop-track') setSession('playback');
    }
    const settle = Number(this.config.settleMs);
    if (settle > 0) {
      log(`waiting ${String(settle)} ms`);
      await new Promise((resolve) => {
        setTimeout(resolve, settle);
      });
    }
    diag['session while playing'] = sessionType();
    diag['before the sound'] = `${(performance.now() - t).toFixed(0)} ms`;
  }

  /** Undoes `duringPlayback` after the sound: the microphone listens again. */
  private async afterSound(diag: Diagnostics, log: (line: string) => void): Promise<void> {
    const mode = this.config.duringPlayback;
    const track = this.stream?.getAudioTracks()[0];
    if (mode === 'keep' || !track || !this.context) return;
    const t = performance.now();
    if (mode === 'disable-track') track.enabled = true;
    else if (mode === 'ec-off') {
      try {
        await track.applyConstraints(constraints(this.config));
      } catch (e) {
        diag['EC back on'] = `failed: ${String(e)}`;
      }
      diag['EC after'] = String(track.getSettings().echoCancellation ?? '?');
    } else {
      log('reopening the mic');
      if (mode !== 'stop-track') setSession('play-and-record');
      try {
        await this.listener?.close();
        this.listener = null;
        this.stream = await navigator.mediaDevices.getUserMedia({
          audio: constraints(this.config),
        });
        if (this.config.worklet !== 'none')
          this.listener = await Listener.start(
            this.stream,
            {},
            this.captureContext ?? this.context,
          );
      } catch (e) {
        this.stream = null;
        diag.reopen = `failed: ${String(e)}`;
      }
    }
    diag['mic listening again after'] = `${(performance.now() - t).toFixed(0)} ms`;
  }

  stop(): void {
    this.closeOutput();
    this.stopPlayback?.();
    this.stopPlayback = null;
  }

  /** Stops the tracks first, then lets go of the context (only one the lab made is closed). */
  async teardown(): Promise<void> {
    this.stop();
    await this.listener?.close();
    this.listener = null;
    for (const track of this.stream?.getTracks() ?? []) track.stop();
    this.stream = null;
    if (this.context && this.owned) {
      this.libraries.delete(this.context);
      await this.context.close();
    }
    await this.captureContext?.close();
    this.captureContext = null;
    this.context = null;
    this.owned = false;
  }

  get micOpen(): boolean {
    return this.stream !== null;
  }
}

/** A rated run, kept on this device (not part of any profile). */
export interface LabResult {
  at: number;
  preset: string;
  config: LabConfig;
  line: string;
  rating: 'clean' | 'crackly' | 'unsure';
  note: string;
  diag: Diagnostics;
}

export const RESULTS_KEY = 'foe-names.audiolab.v1';

/** A rated run, stamped now. */
export const newResult = (run: Omit<LabResult, 'at'>): LabResult => ({ at: Date.now(), ...run });

export function loadResults(): LabResult[] {
  try {
    return JSON.parse(localStorage.getItem(RESULTS_KEY) ?? '[]') as LabResult[];
  } catch {
    return [];
  }
}

export function saveResults(results: readonly LabResult[]): void {
  try {
    localStorage.setItem(RESULTS_KEY, JSON.stringify(results));
  } catch {
    // storage blocked: results last for this visit
  }
}

/** The changes from the game's setup, e.g. "EC off, sound tone". */
export function describe(config: LabConfig): string {
  const parts = (Object.keys(config) as (keyof LabConfig)[])
    .filter((k) => config[k] !== GAME[k])
    .map((k) => `${k} ${String(config[k])}`);
  return parts.length ? parts.join(', ') : 'as in the game';
}

/** All results as plain text, to paste into a chat or an issue. */
export function resultsText(results: readonly LabResult[], device: string): string {
  return [
    `Audio lab results (${String(results.length)}) — ${device}`,
    ...results.map((r) =>
      [
        `${r.preset} ${r.rating.toUpperCase()} — ${describe(r.config)} — "${r.line}"${r.note ? ` — note: ${r.note}` : ''}`,
        ...Object.entries(r.diag).map(([k, v]) => `    ${k}: ${v}`),
      ].join('\n'),
    ),
  ].join('\n');
}
